import React, { useState, useMemo, useCallback } from 'react'
import * as Sentry from '@sentry/react'
import { useDepartment } from '../context/DepartmentContext'
import {
  getSalesTransactions, getMorningLogs, getSurplus, getAllDailySummaries,
  getDailyCategorySummaryRange, getFlags, getProductionLogs, getDistributionLogs, getSalesInRange
} from '../lib/store'
import { formatR, calcStats, buildDailyCategorySummary, calculateProjectedSales, getAveragePriceForCategory } from '../lib/calculations'
import { getProducts, getPapConfig } from '../data/products'
import { getCategories, getCategoryConfig } from '../data/categories'
import { isMealSale, CROSS_POOL_ALLOC, getDailyPrices, getDynamicCrossPoolAlloc } from '../utils/reconciliationEngine'
import { isVorsSuppressed, vorsDisplayNote } from '../utils/steakModeLogic'
import { computeDiscountShortages } from '../lib/discountShortages'
import { getProductPrice } from '../lib/productPricing'
import DateInput from '../components/ui/DateInput'
import {
  TrendingUp, TrendingDown, BarChart3, ShoppingBag, Trash2, Heart,
  Brain, AlertTriangle, CheckCircle, XCircle,
  Calendar, Database, Target, DollarSign, Package, Activity, ShieldAlert, ChevronDown, ChevronRight,
  Download, FileSpreadsheet
} from 'lucide-react'
import {
  getDateRange, getWeekNumber, getPreviousPeriod, getDayName, getDayOfMonth,
  getMonthKey, formatShortDate, formatMonthLabel, pct, fmtPct, colorForChange,
} from '../utils/dateRangeHelpers'
import {
  Card, KpiCard, SectionTitle, EmptyState, Th, Td, Badge, HorizontalBar, ChangeArrow,
} from '../components/BusinessAnalytics/AnalyticsUIKit'
import PapAnalyticsSection from '../components/BusinessAnalytics/PapAnalyticsSection'

// ─── Tab definitions ───

const TABS = [
  { id: 'revenue', label: 'Revenue', icon: DollarSign },
  { id: 'products', label: 'Products', icon: ShoppingBag },
  { id: 'shortfall', label: 'Shortfall', icon: ShieldAlert },
  { id: 'waste', label: 'Waste', icon: Trash2 },
  { id: 'financial', label: 'Financial', icon: Heart },
  { id: 'ops', label: 'Operations', icon: Brain },
  { id: 'pap', label: 'PAP', icon: Activity },
]

// ─── Main Component ───

export default function BusinessAnalytics() {
  const { currentDepartmentId } = useDepartment()
  const [tab, setTab] = useState('revenue')
  const today = new Date().toISOString().split('T')[0]
  const thirtyAgo = new Date(Date.now() - 30 * 86400000).toISOString().split('T')[0]
  const [startDate, setStartDate] = useState(thirtyAgo)
  const [endDate, setEndDate] = useState(today)

  // Break-even inputs (financial tab)
  const [fixedCosts, setFixedCosts] = useState('')
  const [marginPct, setMarginPct] = useState('')

  const dept = currentDepartmentId
  const isBakery = dept === 'bakery' || dept === 'yawehs'

  // ─── Collect all data for the range ───

  const data = useMemo(() => {
    if (!dept) return null
    const dates = getDateRange(startDate, endDate)
    const products = getProducts(dept)
    const productMap = Object.fromEntries(products.map(p => [p.code, p]))
    const prev = getPreviousPeriod(startDate, endDate)

    // Daily data collection
    const dailyRevenue = {}
    const allSales = []
    const dailyMorning = {}
    const dailySurplus = {}
    const dailySalesData = {}

    for (const date of dates) {
      const sales = getSalesTransactions(dept, date)
      const dayTotal = sales.reduce((s, t) => s + (t.salesRands || 0), 0)
      dailyRevenue[date] = dayTotal
      dailySalesData[date] = sales
      allSales.push(...sales)

      const morning = getMorningLogs(dept, date)
      dailyMorning[date] = morning

      const surplus = getSurplus(dept, date)
      dailySurplus[date] = surplus
    }

    // Previous period
    const prevDates = getDateRange(prev.start, prev.end)
    let prevTotalRevenue = 0
    for (const date of prevDates) {
      const sales = getSalesTransactions(dept, date)
      prevTotalRevenue += sales.reduce((s, t) => s + (t.salesRands || 0), 0)
    }

    // Summaries
    const allSummaries = getAllDailySummaries(dept)
      .filter(s => s.date >= startDate && s.date <= endDate)
    const catSummaries = getDailyCategorySummaryRange(dept, startDate, endDate)
    const flags = getFlags(dept, { startDate, endDate })

    // Revenue values
    const revenueValues = dates.map(d => dailyRevenue[d] || 0)
    const totalRevenue = revenueValues.reduce((s, v) => s + v, 0)
    const tradingDays = revenueValues.filter(v => v > 0)
    const stats = calcStats(tradingDays.length > 0 ? tradingDays : [0])
    const growthRate = prevTotalRevenue > 0 ? ((totalRevenue - prevTotalRevenue) / prevTotalRevenue) * 100 : null
    const bestDay = dates.reduce((best, d) => (!best || (dailyRevenue[d] || 0) > (dailyRevenue[best] || 0)) ? d : best, null)

    // Shortfall data: per-day reconciliation — MUST match DailyOps.jsx exactly
    const categories = getCategories()
    const shortfallByDay = []
    const shortfallByCat = {}

    // Discount shortages — separate pipeline from stock shortfalls.
    // Stock shortfall = inventory unaccounted; discount shortage =
    // revenue lost on items physically sold below standard price.
    const discountByDay = []           // [{ date, totalDiscount, totalFreeLoss, items, freeItems }]
    const discountByProduct = {}       // { code: { name, qty, totalDiscount, days } }

    for (const date of dates) {
      const morning = dailyMorning[date] || []
      const sales = dailySalesData[date] || []
      const surplusEntries = dailySurplus[date] || []

      // Cross-pool accumulator (same as DailyOps)
      const crossPoolConsumed = {}

      // Resolve today's piece-cost-driven cross-pool allocation. Per-day,
      // so it lives inside the date loop. Falls back to legacy R30/R18 for
      // historical dates with no cached price.
      const { steakUP, vorsUP, steakMode } = getDailyPrices(dept, date)
      const dynamicAlloc = getDynamicCrossPoolAlloc(steakUP, vorsUP, steakMode)

      let dayProjected = 0, dayConsumed = 0, daySurplus = 0, dayShortfall = 0
      const catConsumedMap = {}

      for (const cat of categories) {
        const opening = morning.find(m => m.category === cat.name)?.value || 0
        const surplusRaw = surplusEntries.find(s => s.category === cat.name)?.value || 0

        // FIX 1: Convert surplus to rands for unit-based categories
        let surplusVal = surplusRaw
        if (cat.surplusUnit === 'units' || cat.openingUnit === 'units') {
          if (cat.type === 'fixed_unit' && cat.unitPrice) {
            surplusVal = surplusRaw * cat.unitPrice
          } else if (cat.type === 'discrete') {
            surplusVal = surplusRaw * getAveragePriceForCategory(cat.name, dept)
          }
        }

        const projected = calculateProjectedSales(cat.name, opening, 0) || 0

        const catSales = sales.filter(s => s.category === cat.name)
        let consumed = 0
        for (const s of catSales) {
          const prod = products.find(p => p.code?.toLowerCase() === s.productCode?.toLowerCase())
          const qty = Number(s.qtySold) || 0
          const actualRands = Number(s.salesRands) || 0

          if (!prod) {
            consumed += actualRands
          } else {
            // FIX 2: Cross-pool allocation for FS/HST. Dynamic alloc (driven
            // by today's piece costs + vors toggle) takes priority over the
            // per-product override and the legacy hardcoded constant.
            const crossAlloc = dynamicAlloc[prod.code] || prod.crossPoolAlloc || CROSS_POOL_ALLOC[prod.code]
            if (crossAlloc) {
              const thisCatAlloc = crossAlloc[cat.name] || 0
              consumed += qty * thisCatAlloc
              for (const [poolName, poolAmount] of Object.entries(crossAlloc)) {
                if (poolName !== cat.name && poolAmount > 0) {
                  if (!crossPoolConsumed[poolName]) crossPoolConsumed[poolName] = 0
                  crossPoolConsumed[poolName] += qty * poolAmount
                }
              }
            } else if (prod.saleType === 'continuous_mirror') {
              // FIX 3: Use actual POS rands for standalone
              consumed += actualRands
            } else if (isMealSale(prod.name, prod.code)) {
              if (prod.saleType === 'continuous_fixed') {
                consumed += qty * (Number(prod.qps) || 0)
              } else {
                // Discrete meal bundle: pool depletion = total pieces ×
                // base piece cost for the category. Matches Path A
                // (reconcileCategory) instead of the legacy 1-piece bug.
                const basePieceCost = getAveragePriceForCategory(cat.name, dept)
                const pieces = qty * (Number(prod.qps) || 1)
                consumed += pieces * basePieceCost
              }
            } else {
              // FIX 3: Standalone = actual POS rands, not qty × unitPrice
              consumed += actualRands
            }
          }
        }

        catConsumedMap[cat.name] = { consumed, projected, surplusVal }
        dayProjected += projected
        daySurplus += surplusVal
      }

      // Second pass: apply cross-pool contributions (same as DailyOps)
      for (const [poolName, amount] of Object.entries(crossPoolConsumed)) {
        if (catConsumedMap[poolName]) {
          catConsumedMap[poolName].consumed += amount
        }
      }

      // Vors suppression: when the kitchen isn't serving vors today
      // (no_vors or extra_steak), label the vors entry with an explanation
      // and clear any shortfall classification for it. The rand math is
      // already correct because dynamicAlloc has vors:0 in non-vors modes,
      // so vors's consumed/projected stay at 0 — we just stop it from
      // appearing as an unexplained "—" row in the analytics view.
      if (isVorsSuppressed(steakMode) && catConsumedMap['vors']) {
        catConsumedMap['vors'] = {
          ...catConsumedMap['vors'],
          displayNote: vorsDisplayNote(steakMode),
          status: null,
        }
      }

      // Compute shortfalls per category after cross-pool
      for (const cat of categories) {
        const c = catConsumedMap[cat.name]
        if (!c) continue
        dayConsumed += c.consumed

        // Skip shortfall computation for the vors row on non-vors days —
        // there's no pool to be short against.
        if (cat.name === 'vors' && isVorsSuppressed(steakMode)) continue

        if (c.projected > 0) {
          const diff = c.projected - c.consumed - c.surplusVal
          if (diff > 2) {
            dayShortfall += diff
            if (!shortfallByCat[cat.name]) shortfallByCat[cat.name] = { label: cat.label, days: [], totalShort: 0 }
            shortfallByCat[cat.name].days.push({ date, diff, projected: c.projected, consumed: c.consumed, surplus: c.surplusVal })
            shortfallByCat[cat.name].totalShort += diff
          }
        }
      }

      shortfallByDay.push({ date, projected: dayProjected, consumed: dayConsumed, surplus: daySurplus, diff: dayShortfall, status: dayShortfall > 2 ? 'short' : 'reconciled' })

      // Discount shortages for this day. Pure helper — same one used by
      // the reconciliation engine and DailyOps so the numbers always
      // agree across the three surfaces.
      const dayDiscounts = computeDiscountShortages(sales, products, dept)
      discountByDay.push({
        date,
        totalDiscount: dayDiscounts.totalDiscount,
        totalFreeLoss: dayDiscounts.totalFreeLoss,
        affectedProducts: dayDiscounts.affectedProducts,
        items: dayDiscounts.items,
        freeItems: dayDiscounts.freeItems,
      })
      // Per-product roll-up across the period
      for (const item of dayDiscounts.items) {
        const key = item.productCode
        if (!discountByProduct[key]) {
          discountByProduct[key] = {
            productCode: item.productCode,
            productName: item.productName,
            qty: 0,
            totalDiscount: 0,
            days: 0,
          }
        }
        discountByProduct[key].qty += item.qty
        discountByProduct[key].totalDiscount += item.totalDiscount
        discountByProduct[key].days += 1
      }
    }

    return {
      dates, products, productMap, prev, dailyRevenue, allSales, dailyMorning, dailySurplus,
      dailySalesData, prevTotalRevenue, allSummaries, catSummaries, flags,
      revenueValues, totalRevenue, tradingDays, stats, growthRate, bestDay,
      shortfallByDay, shortfallByCat,
      discountByDay, discountByProduct,
    }
  }, [dept, startDate, endDate])

  if (!dept) {
    return (
      <div className="min-h-screen bg-gray-950 flex items-center justify-center">
        <p className="text-gray-400">Select a department first</p>
      </div>
    )
  }

  if (!data) return null

  // ─── TAB 1: REVENUE INTELLIGENCE ───

  function RevenueTab() {
    const { dates, dailyRevenue, totalRevenue, stats, growthRate, bestDay, allSales } = data

    // Last 14 days trend
    const last14 = dates.slice(-14)
    const trendRows = last14.map((d, i) => {
      const rev = dailyRevenue[d] || 0
      const prevRev = i > 0 ? (dailyRevenue[last14[i - 1]] || 0) : null
      const change = prevRev !== null && prevRev > 0 ? ((rev - prevRev) / prevRev) * 100 : null
      return { date: d, revenue: rev, change }
    })

    // Day-of-week performance
    const dowData = {}
    const DOW_ORDER = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']
    for (const d of dates) {
      const dow = getDayName(d)
      if (!dowData[dow]) dowData[dow] = { revenues: [], sales: [], categories: {} }
      dowData[dow].revenues.push(dailyRevenue[d] || 0)
      const daySales = data.dailySalesData[d] || []
      dowData[dow].sales.push(daySales.length)
      for (const s of daySales) {
        const cat = s.category || 'unknown'
        dowData[dow].categories[cat] = (dowData[dow].categories[cat] || 0) + (s.salesRands || 0)
      }
    }
    const dowRows = DOW_ORDER.map(dow => {
      const info = dowData[dow]
      if (!info) return { dow, avg: 0, txCount: 0, bestCat: '--' }
      const avg = info.revenues.reduce((s, v) => s + v, 0) / info.revenues.length
      const txCount = info.sales.reduce((s, v) => s + v, 0)
      const bestCat = Object.entries(info.categories).sort((a, b) => b[1] - a[1])[0]?.[0] || '--'
      return { dow, avg, txCount, bestCat }
    })

    // Weekly summary
    const weekMap = {}
    for (const d of dates) {
      const wk = `${new Date(d + 'T00:00:00').getFullYear()}-W${String(getWeekNumber(d)).padStart(2, '0')}`
      if (!weekMap[wk]) weekMap[wk] = { total: 0, days: 0 }
      weekMap[wk].total += dailyRevenue[d] || 0
      weekMap[wk].days++
    }
    const weekKeys = Object.keys(weekMap).sort()
    const weekRows = weekKeys.map((wk, i) => {
      const { total, days } = weekMap[wk]
      const prevTotal = i > 0 ? weekMap[weekKeys[i - 1]].total : null
      const change = prevTotal && prevTotal > 0 ? ((total - prevTotal) / prevTotal) * 100 : null
      return { week: wk, total, dailyAvg: total / days, change }
    })

    // Monthly comparison
    const monthMap = {}
    for (const d of dates) {
      const mk = getMonthKey(d)
      if (!monthMap[mk]) monthMap[mk] = 0
      monthMap[mk] += dailyRevenue[d] || 0
    }
    const monthKeys = Object.keys(monthMap).sort()
    const monthRows = monthKeys.map((mk, i) => {
      const total = monthMap[mk]
      const prevTotal = i > 0 ? monthMap[monthKeys[i - 1]] : null
      const growth = prevTotal && prevTotal > 0 ? ((total - prevTotal) / prevTotal) * 100 : null
      return { month: mk, total, growth }
    })

    // Payday insight: days 25-5 vs days 6-24
    let paydayRevenue = 0
    let nonPaydayRevenue = 0
    let paydayDays = 0
    let nonPaydayDays = 0
    for (const d of dates) {
      const dom = getDayOfMonth(d)
      const rev = dailyRevenue[d] || 0
      if (dom >= 25 || dom <= 5) {
        paydayRevenue += rev
        paydayDays++
      } else {
        nonPaydayRevenue += rev
        nonPaydayDays++
      }
    }

    return (
      <div>
        {/* KPI Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
          <KpiCard label="Total Revenue" value={formatR(totalRevenue)} icon={DollarSign} color="text-orange-400" />
          <KpiCard label="Daily Average" value={formatR(stats.avg)} sub={`${data.tradingDays.length} trading days`} icon={BarChart3} />
          <KpiCard
            label="Growth Rate"
            value={growthRate !== null ? `${growthRate >= 0 ? '+' : ''}${growthRate.toFixed(1)}%` : '--'}
            sub="vs previous period"
            icon={growthRate >= 0 ? TrendingUp : TrendingDown}
            color={growthRate > 0 ? 'text-green-400' : growthRate < 0 ? 'text-red-400' : 'text-white'}
          />
          <KpiCard label="Best Day" value={bestDay ? formatR(dailyRevenue[bestDay]) : '--'} sub={bestDay ? formatShortDate(bestDay) : ''} icon={Calendar} color="text-amber-400" />
        </div>

        {/* Revenue Trend (14 days) */}
        <SectionTitle>Revenue Trend (Last 14 Days)</SectionTitle>
        <Card className="overflow-x-auto">
          {trendRows.length === 0 ? <EmptyState /> : (
            <table className="w-full text-sm">
              <thead>
                <tr>
                  <Th>Date</Th>
                  <Th>Day</Th>
                  <Th className="text-right">Revenue</Th>
                  <Th className="text-right">Change</Th>
                </tr>
              </thead>
              <tbody>
                {trendRows.map(r => (
                  <tr key={r.date} className="hover:bg-gray-800/50">
                    <Td>{formatShortDate(r.date)}</Td>
                    <Td>{getDayName(r.date)}</Td>
                    <Td className="text-right font-medium text-white">{formatR(r.revenue)}</Td>
                    <Td className={`text-right ${colorForChange(r.change)}`}>
                      {r.change !== null ? (
                        <span className="inline-flex items-center gap-1">
                          <ChangeArrow value={r.change} />
                          {Math.abs(r.change).toFixed(1)}%
                        </span>
                      ) : '--'}
                    </Td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Card>

        {/* Day-of-Week Performance */}
        <SectionTitle>Day-of-Week Performance</SectionTitle>
        <Card className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr>
                <Th>Day</Th>
                <Th className="text-right">Avg Revenue</Th>
                <Th className="text-right">Transactions</Th>
                <Th>Best Category</Th>
              </tr>
            </thead>
            <tbody>
              {dowRows.map(r => (
                <tr key={r.dow} className="hover:bg-gray-800/50">
                  <Td className="font-medium text-white">{r.dow}</Td>
                  <Td className="text-right">{formatR(r.avg)}</Td>
                  <Td className="text-right">{r.txCount}</Td>
                  <Td className="text-gray-400 text-xs">{r.bestCat}</Td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>

        {/* Weekly Summary */}
        <SectionTitle>Weekly Summary</SectionTitle>
        <Card className="overflow-x-auto">
          {weekRows.length === 0 ? <EmptyState /> : (
            <table className="w-full text-sm">
              <thead>
                <tr>
                  <Th>Week</Th>
                  <Th className="text-right">Total</Th>
                  <Th className="text-right">Daily Avg</Th>
                  <Th className="text-right">Change</Th>
                </tr>
              </thead>
              <tbody>
                {weekRows.map(r => (
                  <tr key={r.week} className="hover:bg-gray-800/50">
                    <Td className="font-medium text-white">{r.week}</Td>
                    <Td className="text-right">{formatR(r.total)}</Td>
                    <Td className="text-right text-gray-400">{formatR(r.dailyAvg)}</Td>
                    <Td className={`text-right ${colorForChange(r.change)}`}>
                      {r.change !== null ? (
                        <span className="inline-flex items-center gap-1">
                          <ChangeArrow value={r.change} />
                          {Math.abs(r.change).toFixed(1)}%
                        </span>
                      ) : '--'}
                    </Td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Card>

        {/* Monthly Comparison */}
        {monthKeys.length > 1 && (
          <>
            <SectionTitle>Monthly Comparison</SectionTitle>
            <Card className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr>
                    <Th>Month</Th>
                    <Th className="text-right">Revenue</Th>
                    <Th className="text-right">Growth</Th>
                  </tr>
                </thead>
                <tbody>
                  {monthRows.map(r => (
                    <tr key={r.month} className="hover:bg-gray-800/50">
                      <Td className="font-medium text-white">{formatMonthLabel(r.month)}</Td>
                      <Td className="text-right">{formatR(r.total)}</Td>
                      <Td className={`text-right ${colorForChange(r.growth)}`}>
                        {r.growth !== null ? (
                          <span className="inline-flex items-center gap-1">
                            <ChangeArrow value={r.growth} />
                            {Math.abs(r.growth).toFixed(1)}%
                          </span>
                        ) : '--'}
                      </Td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Card>
          </>
        )}

        {/* Payday Insight */}
        <SectionTitle>Payday Insight (25th-5th vs 6th-24th)</SectionTitle>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Card>
            <p className="text-gray-400 text-xs mb-1">Payday Window (25th-5th)</p>
            <p className="text-xl font-bold text-orange-400">{formatR(paydayRevenue)}</p>
            <p className="text-gray-500 text-xs mt-1">{paydayDays} days | Avg {formatR(paydayDays > 0 ? paydayRevenue / paydayDays : 0)}/day</p>
          </Card>
          <Card>
            <p className="text-gray-400 text-xs mb-1">Non-Payday (6th-24th)</p>
            <p className="text-xl font-bold text-white">{formatR(nonPaydayRevenue)}</p>
            <p className="text-gray-500 text-xs mt-1">{nonPaydayDays} days | Avg {formatR(nonPaydayDays > 0 ? nonPaydayRevenue / nonPaydayDays : 0)}/day</p>
          </Card>
        </div>
        {paydayDays > 0 && nonPaydayDays > 0 && (
          <p className="text-gray-400 text-xs mt-2 ml-1">
            Payday avg is{' '}
            <span className={paydayRevenue / paydayDays > nonPaydayRevenue / nonPaydayDays ? 'text-green-400' : 'text-red-400'}>
              {Math.abs(((paydayRevenue / paydayDays) / (nonPaydayRevenue / nonPaydayDays) - 1) * 100).toFixed(0)}%
              {paydayRevenue / paydayDays > nonPaydayRevenue / nonPaydayDays ? ' higher' : ' lower'}
            </span>
            {' '}than non-payday avg
          </p>
        )}
      </div>
    )
  }

  // ─── TAB 2: PRODUCT INTELLIGENCE ───

  function ProductsTab() {
    const { allSales, totalRevenue, productMap } = data

    // Aggregate by product
    const prodAgg = {}
    for (const s of allSales) {
      const key = s.productCode || s.productName || 'unknown'
      if (!prodAgg[key]) prodAgg[key] = { code: key, name: s.productName || key, category: s.category || '', revenue: 0, qty: 0 }
      prodAgg[key].revenue += s.salesRands || 0
      prodAgg[key].qty += s.qtySold || 0
      // Prefer product name from productMap
      if (productMap[key]) prodAgg[key].name = productMap[key].name
    }

    const sorted = Object.values(prodAgg).sort((a, b) => b.revenue - a.revenue)
    let cumulative = 0
    const withABC = sorted.map((p, i) => {
      cumulative += p.revenue
      const cumPct = pct(cumulative, totalRevenue)
      const abcClass = cumPct <= 80 ? 'A' : cumPct <= 95 ? 'B' : 'C'
      return { ...p, rank: i + 1, pctOfTotal: pct(p.revenue, totalRevenue), cumulativePct: cumPct, abcClass }
    })

    const top20 = withABC.slice(0, 20)
    const bottom10 = [...withABC].reverse().slice(0, 10)

    // Category breakdown
    const catAgg = {}
    for (const s of allSales) {
      const cat = s.category || 'unknown'
      if (!catAgg[cat]) catAgg[cat] = { category: cat, revenue: 0, txCount: 0 }
      catAgg[cat].revenue += s.salesRands || 0
      catAgg[cat].txCount++
    }
    const catSorted = Object.values(catAgg).sort((a, b) => b.revenue - a.revenue)
    const maxCatRev = catSorted[0]?.revenue || 1

    // Product velocity (by qty)
    const velocity = Object.values(prodAgg).sort((a, b) => b.qty - a.qty).slice(0, 20)

    return (
      <div>
        {/* Top 20 by Revenue */}
        <SectionTitle>Top 20 Products by Revenue</SectionTitle>
        <Card className="overflow-x-auto">
          {top20.length === 0 ? <EmptyState /> : (
            <table className="w-full text-sm">
              <thead>
                <tr>
                  <Th>#</Th>
                  <Th>Product</Th>
                  <Th>Category</Th>
                  <Th className="text-right">Qty</Th>
                  <Th className="text-right">Revenue</Th>
                  <Th className="text-right">% Total</Th>
                  <Th className="text-right">Cum %</Th>
                  <Th>Class</Th>
                </tr>
              </thead>
              <tbody>
                {top20.map(p => (
                  <tr key={p.code} className="hover:bg-gray-800/50">
                    <Td className="text-gray-500">{p.rank}</Td>
                    <Td className="font-medium text-white max-w-[140px] truncate">{p.name}</Td>
                    <Td className="text-gray-400 text-xs">{p.category}</Td>
                    <Td className="text-right">{p.qty}</Td>
                    <Td className="text-right font-medium text-white">{formatR(p.revenue)}</Td>
                    <Td className="text-right text-gray-400">{fmtPct(p.pctOfTotal)}</Td>
                    <Td className="text-right text-gray-400">{fmtPct(p.cumulativePct)}</Td>
                    <Td>
                      <Badge
                        label={p.abcClass}
                        color={p.abcClass === 'A' ? 'green' : p.abcClass === 'B' ? 'amber' : 'red'}
                      />
                    </Td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Card>

        {/* Bottom 10 */}
        <SectionTitle>Bottom 10 Products (Removal Candidates)</SectionTitle>
        <Card className="overflow-x-auto">
          {bottom10.length === 0 ? <EmptyState /> : (
            <table className="w-full text-sm">
              <thead>
                <tr>
                  <Th>Product</Th>
                  <Th>Category</Th>
                  <Th className="text-right">Qty</Th>
                  <Th className="text-right">Revenue</Th>
                  <Th className="text-right">% Total</Th>
                </tr>
              </thead>
              <tbody>
                {bottom10.map(p => (
                  <tr key={p.code} className="hover:bg-gray-800/50">
                    <Td className="font-medium text-white max-w-[140px] truncate">{p.name}</Td>
                    <Td className="text-gray-400 text-xs">{p.category}</Td>
                    <Td className="text-right">{p.qty}</Td>
                    <Td className="text-right text-red-400">{formatR(p.revenue)}</Td>
                    <Td className="text-right text-gray-500">{fmtPct(p.pctOfTotal)}</Td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Card>

        {/* Category Breakdown */}
        <SectionTitle>Category Breakdown</SectionTitle>
        <Card className="overflow-x-auto">
          {catSorted.length === 0 ? <EmptyState /> : (
            <table className="w-full text-sm">
              <thead>
                <tr>
                  <Th>Category</Th>
                  <Th className="text-right">Revenue</Th>
                  <Th className="text-right">Share</Th>
                  <Th className="text-right">Avg / Tx</Th>
                  <Th className="w-24">Bar</Th>
                </tr>
              </thead>
              <tbody>
                {catSorted.map(c => (
                  <tr key={c.category} className="hover:bg-gray-800/50">
                    <Td className="font-medium text-white">{c.category}</Td>
                    <Td className="text-right">{formatR(c.revenue)}</Td>
                    <Td className="text-right text-gray-400">{fmtPct(pct(c.revenue, totalRevenue))}</Td>
                    <Td className="text-right text-gray-400">{formatR(c.txCount > 0 ? c.revenue / c.txCount : 0)}</Td>
                    <Td><HorizontalBar value={c.revenue} max={maxCatRev} /></Td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Card>

        {/* Product Velocity */}
        <SectionTitle>Product Velocity (Top 20 by Qty Sold)</SectionTitle>
        <Card className="overflow-x-auto">
          {velocity.length === 0 ? <EmptyState /> : (
            <table className="w-full text-sm">
              <thead>
                <tr>
                  <Th>#</Th>
                  <Th>Product</Th>
                  <Th>Category</Th>
                  <Th className="text-right">Qty Sold</Th>
                  <Th className="text-right">Revenue</Th>
                </tr>
              </thead>
              <tbody>
                {velocity.map((p, i) => (
                  <tr key={p.code} className="hover:bg-gray-800/50">
                    <Td className="text-gray-500">{i + 1}</Td>
                    <Td className="font-medium text-white max-w-[140px] truncate">{p.name}</Td>
                    <Td className="text-gray-400 text-xs">{p.category}</Td>
                    <Td className="text-right font-medium text-orange-400">{p.qty}</Td>
                    <Td className="text-right">{formatR(p.revenue)}</Td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Card>
      </div>
    )
  }

  // ─── TAB 3: WASTE & EFFICIENCY ───

  function WasteTab() {
    const { dates, dailySurplus, dailyMorning, allSummaries, productMap } = data

    // Total waste cost: sum surplus values across all days
    // For continuous_variable categories, surplus is already in rands
    // For fixed_unit, surplus * unitPrice
    let totalWasteCost = 0
    const catWaste = {}

    for (const date of dates) {
      const surps = dailySurplus[date] || []
      for (const s of surps) {
        let wasteVal = 0
        // Find product info from category
        const cat = s.category
        const matchingProducts = data.products.filter(p => p.category === cat)
        if (s.unit === 'rands' || s.unit === 'R') {
          wasteVal = s.value || 0
        } else if (matchingProducts.length > 0) {
          const wastePrice = getProductPrice(matchingProducts[0], dept)
          if (wastePrice) wasteVal = (s.value || 0) * wastePrice
          else wasteVal = s.value || 0
        } else {
          wasteVal = s.value || 0
        }
        totalWasteCost += wasteVal
        catWaste[cat] = (catWaste[cat] || 0) + wasteVal
      }
    }

    const topWasteCategories = Object.entries(catWaste)
      .map(([cat, val]) => ({ category: cat, value: val }))
      .sort((a, b) => b.value - a.value)
      .slice(0, 5)

    // Waste % trend by week
    const weekWaste = {}
    for (const s of allSummaries) {
      if (s.wastePct === null || s.wastePct === undefined) continue
      const wk = `${new Date(s.date + 'T00:00:00').getFullYear()}-W${String(getWeekNumber(s.date)).padStart(2, '0')}`
      if (!weekWaste[wk]) weekWaste[wk] = []
      weekWaste[wk].push(s.wastePct)
    }
    const weekWasteRows = Object.entries(weekWaste)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([wk, vals]) => ({
        week: wk,
        avgWaste: vals.reduce((s, v) => s + v, 0) / vals.length
      }))

    // Days with zero waste vs days with waste
    let zeroDays = 0
    let wasteDays = 0
    for (const date of dates) {
      const surps = dailySurplus[date] || []
      const hasWaste = surps.some(s => s.value > 0)
      if (hasWaste) wasteDays++
      else zeroDays++
    }

    // Production efficiency (bakery only)
    let efficiencyRows = []
    if (isBakery) {
      const prodAgg = {}
      const soldAgg = {}
      for (const date of dates) {
        const prodLogs = getProductionLogs(date, null, dept)
        for (const p of prodLogs) {
          prodAgg[p.productCode] = (prodAgg[p.productCode] || 0) + (p.qtyProduced || 0)
        }
        const sales = data.dailySalesData[date] || []
        for (const s of sales) {
          soldAgg[s.productCode] = (soldAgg[s.productCode] || 0) + (s.qtySold || 0)
        }
      }
      efficiencyRows = Object.keys(prodAgg)
        .map(code => {
          const produced = prodAgg[code]
          const sold = soldAgg[code] || 0
          const ratio = produced > 0 ? (sold / produced) * 100 : 0
          const name = productMap[code]?.name || code
          return { code, name, produced, sold, ratio }
        })
        .sort((a, b) => a.ratio - b.ratio)
        .slice(0, 15)
    }

    // Overproduction indicator: categories where surplus > 20% of opening consistently
    const overProdCats = {}
    for (const date of dates) {
      const morning = dailyMorning[date] || []
      const surplus = dailySurplus[date] || []
      for (const s of surplus) {
        const open = morning.find(m => m.category === s.category)
        if (open && open.value > 0 && s.value > open.value * 0.2) {
          if (!overProdCats[s.category]) overProdCats[s.category] = { count: 0, total: 0 }
          overProdCats[s.category].count++
          overProdCats[s.category].total++
        } else if (open && open.value > 0) {
          if (!overProdCats[s.category]) overProdCats[s.category] = { count: 0, total: 0 }
          overProdCats[s.category].total++
        }
      }
    }
    const overProdRows = Object.entries(overProdCats)
      .filter(([, v]) => v.total >= 3 && v.count / v.total >= 0.5)
      .map(([cat, v]) => ({ category: cat, occurrences: v.count, totalDays: v.total, rate: (v.count / v.total) * 100 }))
      .sort((a, b) => b.rate - a.rate)

    return (
      <div>
        {/* KPIs */}
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 mb-6">
          <KpiCard label="Total Waste Cost" value={formatR(totalWasteCost)} icon={Trash2} color="text-red-400" />
          <KpiCard label="Zero Waste Days" value={`${zeroDays} / ${dates.length}`} sub={fmtPct(pct(zeroDays, dates.length))} icon={CheckCircle} color="text-green-400" />
          <KpiCard label="Waste Days" value={`${wasteDays}`} icon={AlertTriangle} color="text-amber-400" />
        </div>

        {/* Top 5 waste categories */}
        <SectionTitle>Top 5 Categories by Waste Value</SectionTitle>
        <Card className="overflow-x-auto">
          {topWasteCategories.length === 0 ? <EmptyState message="No waste data recorded" /> : (
            <table className="w-full text-sm">
              <thead>
                <tr>
                  <Th>Category</Th>
                  <Th className="text-right">Waste Value</Th>
                  <Th className="text-right">% of Total Waste</Th>
                </tr>
              </thead>
              <tbody>
                {topWasteCategories.map(c => (
                  <tr key={c.category} className="hover:bg-gray-800/50">
                    <Td className="font-medium text-white">{c.category}</Td>
                    <Td className="text-right text-red-400">{formatR(c.value)}</Td>
                    <Td className="text-right text-gray-400">{fmtPct(pct(c.value, totalWasteCost))}</Td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Card>

        {/* Weekly waste % trend */}
        <SectionTitle>Weekly Average Waste %</SectionTitle>
        <Card className="overflow-x-auto">
          {weekWasteRows.length === 0 ? <EmptyState message="No daily summaries with waste % found" /> : (
            <table className="w-full text-sm">
              <thead>
                <tr>
                  <Th>Week</Th>
                  <Th className="text-right">Avg Waste %</Th>
                  <Th className="w-24">Level</Th>
                </tr>
              </thead>
              <tbody>
                {weekWasteRows.map(r => (
                  <tr key={r.week} className="hover:bg-gray-800/50">
                    <Td className="font-medium text-white">{r.week}</Td>
                    <Td className={`text-right ${r.avgWaste > 15 ? 'text-red-400' : r.avgWaste > 8 ? 'text-amber-400' : 'text-green-400'}`}>
                      {fmtPct(r.avgWaste)}
                    </Td>
                    <Td>
                      <Badge
                        label={r.avgWaste > 15 ? 'High' : r.avgWaste > 8 ? 'Medium' : 'Low'}
                        color={r.avgWaste > 15 ? 'red' : r.avgWaste > 8 ? 'amber' : 'green'}
                      />
                    </Td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Card>

        {/* Overproduction indicator */}
        {overProdRows.length > 0 && (
          <>
            <SectionTitle>Overproduction Alert (Surplus &gt; 20% of Opening)</SectionTitle>
            <Card className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr>
                    <Th>Category</Th>
                    <Th className="text-right">Occurrences</Th>
                    <Th className="text-right">Total Days</Th>
                    <Th className="text-right">Rate</Th>
                  </tr>
                </thead>
                <tbody>
                  {overProdRows.map(r => (
                    <tr key={r.category} className="hover:bg-gray-800/50">
                      <Td className="font-medium text-white">{r.category}</Td>
                      <Td className="text-right text-amber-400">{r.occurrences}</Td>
                      <Td className="text-right text-gray-400">{r.totalDays}</Td>
                      <Td className="text-right text-red-400">{fmtPct(r.rate)}</Td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Card>
          </>
        )}

        {/* Production efficiency (bakery) */}
        {isBakery && (
          <>
            <SectionTitle>Production Efficiency (Produced vs Sold)</SectionTitle>
            <Card className="overflow-x-auto">
              {efficiencyRows.length === 0 ? <EmptyState message="No production data for this period" /> : (
                <table className="w-full text-sm">
                  <thead>
                    <tr>
                      <Th>Product</Th>
                      <Th className="text-right">Produced</Th>
                      <Th className="text-right">Sold</Th>
                      <Th className="text-right">Sold %</Th>
                      <Th>Status</Th>
                    </tr>
                  </thead>
                  <tbody>
                    {efficiencyRows.map(r => (
                      <tr key={r.code} className="hover:bg-gray-800/50">
                        <Td className="font-medium text-white max-w-[140px] truncate">{r.name}</Td>
                        <Td className="text-right">{r.produced}</Td>
                        <Td className="text-right">{r.sold}</Td>
                        <Td className={`text-right ${r.ratio >= 80 ? 'text-green-400' : r.ratio >= 60 ? 'text-amber-400' : 'text-red-400'}`}>
                          {fmtPct(r.ratio)}
                        </Td>
                        <Td>
                          <Badge
                            label={r.ratio >= 80 ? 'Good' : r.ratio >= 60 ? 'Fair' : 'Low'}
                            color={r.ratio >= 80 ? 'green' : r.ratio >= 60 ? 'amber' : 'red'}
                          />
                        </Td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </Card>
          </>
        )}
      </div>
    )
  }

  // ─── TAB 4: FINANCIAL HEALTH ───

  function FinancialTab() {
    const { allSales, totalRevenue, dates, dailyRevenue, tradingDays } = data

    // Revenue composition by category
    const catAgg = {}
    for (const s of allSales) {
      const cat = s.category || 'unknown'
      catAgg[cat] = (catAgg[cat] || 0) + (s.salesRands || 0)
    }
    const catComposition = Object.entries(catAgg)
      .map(([cat, rev]) => ({ category: cat, revenue: rev, share: pct(rev, totalRevenue) }))
      .sort((a, b) => b.revenue - a.revenue)
    const maxShare = catComposition[0]?.share || 1

    // Revenue per trading day
    const revenuePerTradingDay = tradingDays.length > 0 ? totalRevenue / tradingDays.length : 0

    // Operating days vs calendar days
    const calendarDays = dates.length
    const operatingDays = tradingDays.length

    // Break-even calculation
    const fc = parseFloat(fixedCosts) || 0
    const mp = parseFloat(marginPct) || 0
    const dailyBreakEven = mp > 0 ? (fc / (mp / 100)) / 30 : 0
    const monthlyBreakEven = mp > 0 ? fc / (mp / 100) : 0

    return (
      <div>
        {/* Revenue Composition */}
        <SectionTitle>Revenue Composition by Category</SectionTitle>
        <Card className="overflow-x-auto">
          {catComposition.length === 0 ? <EmptyState /> : (
            <table className="w-full text-sm">
              <thead>
                <tr>
                  <Th>Category</Th>
                  <Th className="text-right">Revenue</Th>
                  <Th className="text-right">Share</Th>
                  <Th className="w-32">Distribution</Th>
                </tr>
              </thead>
              <tbody>
                {catComposition.map(c => (
                  <tr key={c.category} className="hover:bg-gray-800/50">
                    <Td className="font-medium text-white">{c.category}</Td>
                    <Td className="text-right">{formatR(c.revenue)}</Td>
                    <Td className="text-right text-gray-400">{fmtPct(c.share)}</Td>
                    <Td>
                      <div className="w-full bg-gray-800 rounded-full h-3 overflow-hidden">
                        <div
                          className="h-full rounded-full bg-orange-500"
                          style={{ width: `${(c.share / maxShare) * 100}%` }}
                        />
                      </div>
                    </Td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Card>

        {/* Gross Margin */}
        <SectionTitle>Gross Margin Estimate</SectionTitle>
        <Card>
          <div className="flex items-center gap-3 text-gray-400 text-sm py-4">
            <Target size={20} className="text-gray-600" />
            <p>Set up costing in the Costing Engine to see margin analysis here.</p>
          </div>
        </Card>

        {/* Break-Even Calculator */}
        <SectionTitle>Break-Even Calculator</SectionTitle>
        <Card className="p-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
            <div>
              <label className="text-gray-400 text-xs block mb-1">Fixed Monthly Costs (R)</label>
              <input
                type="number"
                value={fixedCosts}
                onChange={e => setFixedCosts(e.target.value)}
                placeholder="e.g. 25000"
                className="bg-gray-800 border border-gray-700 rounded px-3 py-2 text-sm text-white w-full outline-none focus:border-orange-500"
              />
            </div>
            <div>
              <label className="text-gray-400 text-xs block mb-1">Average Margin %</label>
              <input
                type="number"
                value={marginPct}
                onChange={e => setMarginPct(e.target.value)}
                placeholder="e.g. 40"
                className="bg-gray-800 border border-gray-700 rounded px-3 py-2 text-sm text-white w-full outline-none focus:border-orange-500"
              />
            </div>
          </div>
          {fc > 0 && mp > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="bg-gray-800 rounded-lg p-4 text-center">
                <p className="text-gray-400 text-xs mb-1">Monthly Revenue to Break Even</p>
                <p className="text-2xl font-bold text-orange-400">{formatR(monthlyBreakEven)}</p>
              </div>
              <div className="bg-gray-800 rounded-lg p-4 text-center">
                <p className="text-gray-400 text-xs mb-1">Daily Revenue Needed</p>
                <p className="text-2xl font-bold text-white">{formatR(dailyBreakEven)}</p>
                <p className="text-xs mt-1">
                  {revenuePerTradingDay >= dailyBreakEven ? (
                    <span className="text-green-400">Currently above break-even</span>
                  ) : (
                    <span className="text-red-400">Currently below break-even by {formatR(dailyBreakEven - revenuePerTradingDay)}/day</span>
                  )}
                </p>
              </div>
            </div>
          ) : (
            <p className="text-gray-500 text-sm text-center">Enter fixed costs and margin % above to calculate break-even.</p>
          )}
        </Card>

        {/* Revenue per trading day + operating days */}
        <SectionTitle>Trading Performance</SectionTitle>
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
          <KpiCard label="Revenue / Trading Day" value={formatR(revenuePerTradingDay)} icon={Activity} color="text-orange-400" />
          <KpiCard label="Operating Days" value={`${operatingDays} / ${calendarDays}`} sub={fmtPct(pct(operatingDays, calendarDays))} icon={Calendar} />
          <KpiCard label="Total Revenue" value={formatR(totalRevenue)} sub={`${calendarDays} calendar days`} icon={DollarSign} />
        </div>
      </div>
    )
  }

  // ─── TAB 5: OPERATIONAL INSIGHTS ───

  function OpsTab() {
    const { dates, dailyMorning, dailySurplus, dailySalesData, flags, catSummaries } = data

    // Data completeness
    const completeness = dates.map(date => {
      const hasMorning = (dailyMorning[date] || []).length > 0
      const hasSales = (dailySalesData[date] || []).length > 0
      const hasSurplus = (dailySurplus[date] || []).length > 0
      let score = 0
      let total = 3
      if (hasMorning) score++
      if (hasSales) score++
      if (hasSurplus) score++

      let hasProduction = false
      let hasDistribution = false
      if (isBakery) {
        const prod = getProductionLogs(date, null, dept)
        const dist = getDistributionLogs(date, null, dept)
        hasProduction = prod.length > 0
        hasDistribution = dist.length > 0
        total += 2
        if (hasProduction) score++
        if (hasDistribution) score++
      }

      return { date, hasMorning, hasSales, hasSurplus, hasProduction, hasDistribution, score, total, pct: (score / total) * 100 }
    })

    const avgCompleteness = completeness.length > 0
      ? completeness.reduce((s, c) => s + c.pct, 0) / completeness.length
      : 0

    // Missing data dates
    const missingDates = completeness.filter(c => c.score === 0)

    // Flag analysis
    const flagsBySeverity = { HIGH: 0, MEDIUM: 0, LOW: 0, INFO: 0 }
    const flagsByCode = {}
    let resolvedCount = 0
    for (const f of flags) {
      flagsBySeverity[f.severity] = (flagsBySeverity[f.severity] || 0) + 1
      flagsByCode[f.flagCode || f.description || 'unknown'] = (flagsByCode[f.flagCode || f.description || 'unknown'] || 0) + 1
      if (f.resolved) resolvedCount++
    }
    const resolutionRate = flags.length > 0 ? (resolvedCount / flags.length) * 100 : 0
    const topFlagTypes = Object.entries(flagsByCode)
      .map(([code, count]) => ({ code, count }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 10)

    // Pricing accuracy: transactions with no pricing flags
    const pricingFlags = flags.filter(f =>
      (f.flagCode || '').toLowerCase().includes('price') ||
      (f.description || '').toLowerCase().includes('price')
    )
    const totalTxCount = Object.values(dailySalesData).reduce((s, arr) => s + arr.length, 0)
    const pricingAccuracy = totalTxCount > 0 ? ((totalTxCount - pricingFlags.length) / totalTxCount) * 100 : 100

    // Forecast accuracy: compare projected to actual from catSummaries
    const forecastRows = []
    const catForecast = {}
    for (const cs of catSummaries) {
      if (cs.projected === null || cs.projected === undefined) continue
      if (!catForecast[cs.category]) catForecast[cs.category] = { projected: 0, actual: 0, days: 0 }
      catForecast[cs.category].projected += cs.projected || 0
      catForecast[cs.category].actual += cs.actual || 0
      catForecast[cs.category].days++
    }
    for (const [cat, v] of Object.entries(catForecast)) {
      const accuracy = v.projected > 0 ? Math.min((v.actual / v.projected) * 100, 100) : null
      forecastRows.push({ category: cat, projected: v.projected, actual: v.actual, accuracy, days: v.days })
    }
    forecastRows.sort((a, b) => (b.accuracy || 0) - (a.accuracy || 0))

    return (
      <div>
        {/* Data Completeness */}
        <SectionTitle>Data Completeness Score</SectionTitle>
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 mb-4">
          <KpiCard
            label="Overall Completeness"
            value={fmtPct(avgCompleteness)}
            icon={Database}
            color={avgCompleteness >= 80 ? 'text-green-400' : avgCompleteness >= 50 ? 'text-amber-400' : 'text-red-400'}
          />
          <KpiCard label="Complete Days" value={`${completeness.filter(c => c.pct === 100).length} / ${dates.length}`} icon={CheckCircle} color="text-green-400" />
          <KpiCard label="Missing Data Days" value={`${missingDates.length}`} icon={XCircle} color={missingDates.length > 0 ? 'text-red-400' : 'text-green-400'} />
        </div>

        <Card className="overflow-x-auto mb-6">
          <table className="w-full text-sm">
            <thead>
              <tr>
                <Th>Date</Th>
                <Th className="text-center">Morning</Th>
                <Th className="text-center">Sales</Th>
                <Th className="text-center">Surplus</Th>
                {isBakery && <Th className="text-center">Production</Th>}
                {isBakery && <Th className="text-center">Distribution</Th>}
                <Th className="text-right">Score</Th>
              </tr>
            </thead>
            <tbody>
              {completeness.slice(-30).map(c => (
                <tr key={c.date} className="hover:bg-gray-800/50">
                  <Td className="text-white">{formatShortDate(c.date)}</Td>
                  <Td className="text-center">{c.hasMorning ? <CheckCircle size={14} className="text-green-400 inline" /> : <XCircle size={14} className="text-red-400/50 inline" />}</Td>
                  <Td className="text-center">{c.hasSales ? <CheckCircle size={14} className="text-green-400 inline" /> : <XCircle size={14} className="text-red-400/50 inline" />}</Td>
                  <Td className="text-center">{c.hasSurplus ? <CheckCircle size={14} className="text-green-400 inline" /> : <XCircle size={14} className="text-red-400/50 inline" />}</Td>
                  {isBakery && <Td className="text-center">{c.hasProduction ? <CheckCircle size={14} className="text-green-400 inline" /> : <XCircle size={14} className="text-red-400/50 inline" />}</Td>}
                  {isBakery && <Td className="text-center">{c.hasDistribution ? <CheckCircle size={14} className="text-green-400 inline" /> : <XCircle size={14} className="text-red-400/50 inline" />}</Td>}
                  <Td className={`text-right font-medium ${c.pct === 100 ? 'text-green-400' : c.pct >= 50 ? 'text-amber-400' : 'text-red-400'}`}>
                    {fmtPct(c.pct)}
                  </Td>
                </tr>
              ))}
            </tbody>
          </table>
          {completeness.length > 30 && (
            <p className="text-gray-500 text-xs text-center mt-2">Showing last 30 days. {completeness.length - 30} earlier days hidden.</p>
          )}
        </Card>

        {/* Missing data calendar */}
        {missingDates.length > 0 && (
          <>
            <SectionTitle>Missing Data Dates</SectionTitle>
            <Card>
              <div className="flex flex-wrap gap-2">
                {missingDates.map(c => (
                  <span key={c.date} className="bg-red-500/10 text-red-400 border border-red-500/20 text-xs px-2 py-1 rounded">
                    {formatShortDate(c.date)} ({getDayName(c.date)})
                  </span>
                ))}
              </div>
            </Card>
          </>
        )}

        {/* Flag Analysis */}
        <SectionTitle>Flag Analysis</SectionTitle>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
          <KpiCard label="Total Flags" value={flags.length} icon={AlertTriangle} />
          <KpiCard label="HIGH Severity" value={flagsBySeverity.HIGH || 0} color="text-red-400" />
          <KpiCard label="Resolution Rate" value={fmtPct(resolutionRate)} color={resolutionRate >= 80 ? 'text-green-400' : 'text-amber-400'} />
          <KpiCard label="Pricing Accuracy" value={fmtPct(pricingAccuracy)} color={pricingAccuracy >= 95 ? 'text-green-400' : 'text-amber-400'} />
        </div>

        {topFlagTypes.length > 0 && (
          <Card className="overflow-x-auto mb-6">
            <p className="text-gray-400 text-xs mb-2 font-medium">Most Common Flag Types</p>
            <table className="w-full text-sm">
              <thead>
                <tr>
                  <Th>Flag Code</Th>
                  <Th className="text-right">Count</Th>
                  <Th className="text-right">% of Total</Th>
                </tr>
              </thead>
              <tbody>
                {topFlagTypes.map(f => (
                  <tr key={f.code} className="hover:bg-gray-800/50">
                    <Td className="font-medium text-white text-xs max-w-[200px] truncate">{f.code}</Td>
                    <Td className="text-right">{f.count}</Td>
                    <Td className="text-right text-gray-400">{fmtPct(pct(f.count, flags.length))}</Td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
        )}

        {/* Forecast Accuracy */}
        {forecastRows.length > 0 && (
          <>
            <SectionTitle>Forecast Accuracy (Projected vs Actual)</SectionTitle>
            <Card className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr>
                    <Th>Category</Th>
                    <Th className="text-right">Projected</Th>
                    <Th className="text-right">Actual</Th>
                    <Th className="text-right">Accuracy</Th>
                    <Th className="text-right">Days</Th>
                  </tr>
                </thead>
                <tbody>
                  {forecastRows.map(r => (
                    <tr key={r.category} className="hover:bg-gray-800/50">
                      <Td className="font-medium text-white">{r.category}</Td>
                      <Td className="text-right text-gray-400">{formatR(r.projected)}</Td>
                      <Td className="text-right">{formatR(r.actual)}</Td>
                      <Td className={`text-right font-medium ${r.accuracy !== null ? (r.accuracy >= 90 ? 'text-green-400' : r.accuracy >= 70 ? 'text-amber-400' : 'text-red-400') : 'text-gray-500'}`}>
                        {r.accuracy !== null ? fmtPct(r.accuracy) : '--'}
                      </Td>
                      <Td className="text-right text-gray-500">{r.days}</Td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Card>
          </>
        )}
      </div>
    )
  }

  // ─── TAB: SHORTFALL ANALYSIS ───

  function ShortfallTab() {
    const { shortfallByDay, shortfallByCat, dates, discountByDay, discountByProduct } = data
    const [expandedCat, setExpandedCat] = useState(null)

    const shortDays = shortfallByDay.filter(d => d.status === 'short')
    const reconciledDays = shortfallByDay.filter(d => d.status === 'reconciled')
    const totalShortfall = shortDays.reduce((s, d) => s + d.diff, 0)
    const avgShortfall = shortDays.length > 0 ? totalShortfall / shortDays.length : 0

    // ── Discount losses analytics ──
    const totalDiscountLoss = (discountByDay || []).reduce((s, d) => s + (d.totalDiscount || 0), 0)
    const totalFreeLoss = (discountByDay || []).reduce((s, d) => s + (d.totalFreeLoss || 0), 0)
    const discountDays = (discountByDay || []).filter(d => d.totalDiscount > 0)
    const avgDailyDiscount = discountDays.length > 0 ? totalDiscountLoss / discountDays.length : 0
    const worstDiscountDay = (discountByDay || []).reduce(
      (worst, d) => (d.totalDiscount > (worst?.totalDiscount || 0) ? d : worst),
      null
    )
    const discountProductRanking = Object.values(discountByProduct || {})
      .sort((a, b) => b.totalDiscount - a.totalDiscount)
    const mostDiscountedProduct = discountProductRanking[0] || null

    // Category ranking
    const catRanking = Object.entries(shortfallByCat)
      .map(([name, c]) => ({
        name, label: c.label, daysShort: c.days.length, totalShort: c.totalShort,
        avgShort: c.totalShort / c.days.length,
        worst: c.days.reduce((w, d) => d.diff > w.diff ? d : w, { diff: 0, date: '' }),
        days: c.days,
      }))
      .sort((a, b) => b.totalShort - a.totalShort)

    // Day of week breakdown
    const dowShortfall = {}
    const DOW = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']
    for (const d of shortfallByDay) {
      const dow = getDayName(d.date)
      if (!dowShortfall[dow]) dowShortfall[dow] = { total: 0, count: 0, shortCount: 0 }
      dowShortfall[dow].count++
      if (d.diff > 2) { dowShortfall[dow].total += d.diff; dowShortfall[dow].shortCount++ }
    }

    // Chart: max shortfall for bar scaling
    const maxShortfall = Math.max(...shortfallByDay.map(d => Math.max(d.diff, 0)), 1)

    return (
      <div className="space-y-6 pb-8">
        {/* KPI Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <KpiCard label="Total Shortfall" value={formatR(totalShortfall)} icon={ShieldAlert} color={totalShortfall > 0 ? 'text-red-400' : 'text-green-400'} />
          <KpiCard label="Days Short" value={`${shortDays.length} / ${dates.length}`} sub={`${reconciledDays.length} reconciled`} icon={Calendar} />
          <KpiCard label="Avg Daily Shortfall" value={formatR(avgShortfall)} sub="on short days only" icon={Target} />
          <KpiCard label="Categories Affected" value={catRanking.length} icon={BarChart3} color={catRanking.length > 0 ? 'text-amber-400' : 'text-green-400'} />
        </div>

        {/* Export buttons */}
        <div className="flex items-center gap-2">
          <button
            onClick={async () => {
              try {
                const { default: jsPDF } = await import('jspdf')
                const { default: autoTable } = await import('jspdf-autotable')
                const doc = new jsPDF()
                const pw = doc.internal.pageSize.width
                const ph = doc.internal.pageSize.height
                const safe = (s) => String(s || '').replace(/[^\x00-\x7F]/g, '')
                const fR = (v) => safe(formatR(v))
                let y = 20

                // Header
                doc.setFontSize(16); doc.setFont('helvetica', 'bold')
                doc.text('DESRES KITCHEN - SHORTFALL ANALYSIS', pw / 2, y, { align: 'center' }); y += 8
                doc.setFontSize(10); doc.setFont('helvetica', 'normal')
                doc.text(`${safe(dept)} | ${startDate} to ${endDate} | ${dates.length} days`, pw / 2, y, { align: 'center' }); y += 10

                // KPIs
                doc.setFontSize(11); doc.setFont('helvetica', 'bold')
                doc.text('KEY METRICS', 14, y); y += 6
                doc.setFontSize(9); doc.setFont('helvetica', 'normal')
                doc.text(`Total Shortfall: ${fR(totalShortfall)}`, 14, y); y += 5
                doc.text(`Days Short: ${shortDays.length} / ${dates.length} (${reconciledDays.length} reconciled)`, 14, y); y += 5
                doc.text(`Avg Daily Shortfall (short days): ${fR(avgShortfall)}`, 14, y); y += 5
                doc.text(`Categories Affected: ${catRanking.length}`, 14, y); y += 10

                // Daily Summary
                doc.setFontSize(11); doc.setFont('helvetica', 'bold')
                doc.text('DAILY SHORTFALL SUMMARY', 14, y); y += 3
                autoTable(doc, {
                  startY: y,
                  head: [['Date', 'Day', 'Projected (R)', 'Consumed (R)', 'Surplus (R)', 'Shortfall (R)', 'Status']],
                  body: shortfallByDay.map(d => [
                    safe(formatShortDate(d.date)), safe(getDayName(d.date)),
                    fR(d.projected), fR(d.consumed), fR(d.surplus),
                    fR(Math.max(d.diff, 0)), d.status === 'short' ? 'SHORT' : 'OK'
                  ]),
                  foot: [['TOTAL', '', fR(shortfallByDay.reduce((s, d) => s + d.projected, 0)),
                    fR(shortfallByDay.reduce((s, d) => s + d.consumed, 0)),
                    fR(shortfallByDay.reduce((s, d) => s + d.surplus, 0)),
                    fR(totalShortfall), '']],
                  styles: { fontSize: 7 },
                  headStyles: { fillColor: [180, 50, 40] },
                  footStyles: { fillColor: [40, 40, 40], fontStyle: 'bold' },
                  margin: { left: 14, right: 14 },
                })
                y = doc.lastAutoTable.finalY + 10

                // Category Breakdown
                if (catRanking.length > 0) {
                  if (y > ph - 60) { doc.addPage(); y = 20 }
                  doc.setFontSize(11); doc.setFont('helvetica', 'bold')
                  doc.text('SHORTFALL BY CATEGORY', 14, y); y += 3
                  autoTable(doc, {
                    startY: y,
                    head: [['Category', 'Days Short', 'Total Short (R)', 'Avg/Day (R)', 'Worst Day (R)', 'Worst Date']],
                    body: catRanking.map(c => [
                      safe(c.label), c.daysShort, fR(c.totalShort), fR(c.avgShort),
                      fR(c.worst.diff), safe(formatShortDate(c.worst.date))
                    ]),
                    styles: { fontSize: 8 },
                    headStyles: { fillColor: [200, 150, 0] },
                    margin: { left: 14, right: 14 },
                  })
                  y = doc.lastAutoTable.finalY + 10

                  // Per-category daily detail
                  for (const c of catRanking) {
                    if (y > ph - 50) { doc.addPage(); y = 20 }
                    doc.setFontSize(9); doc.setFont('helvetica', 'bold')
                    doc.text(`${safe(c.label)} - Daily Detail`, 14, y); y += 3
                    autoTable(doc, {
                      startY: y,
                      head: [['Date', 'Day', 'Projected', 'Consumed', 'Surplus', 'Shortfall']],
                      body: c.days.sort((a, b) => a.date.localeCompare(b.date)).map(d => [
                        safe(formatShortDate(d.date)), safe(getDayName(d.date)),
                        fR(d.projected), fR(d.consumed), fR(d.surplus), fR(d.diff)
                      ]),
                      styles: { fontSize: 7 },
                      headStyles: { fillColor: [60, 60, 60] },
                      margin: { left: 14, right: 14 },
                    })
                    y = doc.lastAutoTable.finalY + 8
                  }
                }

                // Day of Week
                if (y > ph - 50) { doc.addPage(); y = 20 }
                doc.setFontSize(11); doc.setFont('helvetica', 'bold')
                doc.text('SHORTFALL BY DAY OF WEEK', 14, y); y += 3
                autoTable(doc, {
                  startY: y,
                  head: [['Day', 'Avg Shortfall (R)', 'Short Days', 'Total Days']],
                  body: DOW.map(dow => {
                    const d = dowShortfall[dow] || { total: 0, count: 0, shortCount: 0 }
                    const avg = d.shortCount > 0 ? d.total / d.shortCount : 0
                    return [dow, fR(avg), d.shortCount, d.count]
                  }),
                  styles: { fontSize: 8 },
                  headStyles: { fillColor: [60, 60, 60] },
                  margin: { left: 14, right: 14 },
                })

                // Footer
                const pageCount = doc.internal.getNumberOfPages()
                for (let i = 1; i <= pageCount; i++) {
                  doc.setPage(i)
                  doc.setFontSize(7); doc.setTextColor(150)
                  doc.text(`Generated by Desres OS - ${new Date().toLocaleString()}`, pw / 2, ph - 8, { align: 'center' })
                  if (pageCount > 1) doc.text(`Page ${i}/${pageCount}`, pw - 14, ph - 8, { align: 'right' })
                }
                doc.save(`shortfall-analysis-${dept}-${startDate}-to-${endDate}.pdf`)
              } catch (err) {
                console.warn('[BusinessAnalytics] PDF export failed:', err?.message)
                Sentry.captureException(err, { extra: { fn: 'ShortfallTab.exportPdf', dept, startDate, endDate, days: dates.length } })
              }
            }}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs text-gray-400 hover:text-white bg-gray-800 hover:bg-gray-700 border border-gray-700 rounded transition-colors"
          >
            <Download size={13} /> Download PDF
          </button>
          <button
            onClick={async () => {
              try {
                const XLSX = (await import('xlsx')).default || await import('xlsx')
                const wb = XLSX.utils.book_new()

                // Sheet 1: Daily Summary
                const dailyRows = shortfallByDay.map(d => ({
                  Date: d.date, Day: getDayName(d.date),
                  'Projected (R)': Math.round(d.projected), 'Consumed (R)': Math.round(d.consumed),
                  'Surplus (R)': Math.round(d.surplus), 'Shortfall (R)': Math.round(Math.max(d.diff, 0)),
                  Status: d.status === 'short' ? 'SHORT' : 'OK',
                }))
                dailyRows.push({
                  Date: 'TOTAL', Day: `${dates.length} days`,
                  'Projected (R)': Math.round(shortfallByDay.reduce((s, d) => s + d.projected, 0)),
                  'Consumed (R)': Math.round(shortfallByDay.reduce((s, d) => s + d.consumed, 0)),
                  'Surplus (R)': Math.round(shortfallByDay.reduce((s, d) => s + d.surplus, 0)),
                  'Shortfall (R)': Math.round(totalShortfall), Status: '',
                })
                XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(dailyRows), 'Daily Summary')

                // Sheet 2: Category Summary
                if (catRanking.length > 0) {
                  const catRows = catRanking.map(c => ({
                    Category: c.label, 'Days Short': c.daysShort,
                    'Total Short (R)': Math.round(c.totalShort),
                    'Avg Per Day (R)': Math.round(c.avgShort),
                    'Worst Day (R)': Math.round(c.worst.diff),
                    'Worst Date': c.worst.date,
                  }))
                  XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(catRows), 'Category Summary')
                }

                // Sheet 3: Category Detail (all days for all categories)
                const detailRows = []
                for (const c of catRanking) {
                  for (const d of c.days.sort((a, b) => a.date.localeCompare(b.date))) {
                    detailRows.push({
                      Category: c.label, Date: d.date, Day: getDayName(d.date),
                      'Projected (R)': Math.round(d.projected), 'Consumed (R)': Math.round(d.consumed),
                      'Surplus (R)': Math.round(d.surplus), 'Shortfall (R)': Math.round(d.diff),
                    })
                  }
                }
                if (detailRows.length > 0) {
                  XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(detailRows), 'Category Detail')
                }

                // Sheet 4: Day of Week
                const dowRows = DOW.map(dow => {
                  const d = dowShortfall[dow] || { total: 0, count: 0, shortCount: 0 }
                  return {
                    Day: dow, 'Avg Shortfall (R)': Math.round(d.shortCount > 0 ? d.total / d.shortCount : 0),
                    'Short Days': d.shortCount, 'Total Days': d.count,
                  }
                })
                XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(dowRows), 'Day of Week')

                // Sheet 5: KPIs
                const kpiRows = [
                  { Metric: 'Total Shortfall', Value: `R${Math.round(totalShortfall)}` },
                  { Metric: 'Days Short', Value: `${shortDays.length} / ${dates.length}` },
                  { Metric: 'Days Reconciled', Value: reconciledDays.length },
                  { Metric: 'Avg Daily Shortfall (short days)', Value: `R${Math.round(avgShortfall)}` },
                  { Metric: 'Categories Affected', Value: catRanking.length },
                  { Metric: 'Date Range', Value: `${startDate} to ${endDate}` },
                  { Metric: 'Department', Value: dept },
                ]
                XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(kpiRows), 'KPIs')

                XLSX.writeFile(wb, `shortfall-analysis-${dept}-${startDate}-to-${endDate}.xlsx`)
              } catch (err) {
                console.warn('[BusinessAnalytics] Excel export failed:', err?.message)
                Sentry.captureException(err, { extra: { fn: 'ShortfallTab.exportExcel', dept, startDate, endDate, days: dates.length } })
              }
            }}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs text-gray-400 hover:text-white bg-gray-800 hover:bg-gray-700 border border-gray-700 rounded transition-colors"
          >
            <FileSpreadsheet size={13} /> Download Excel
          </button>
        </div>

        {/* SECTION 3 — Shortfall Trend Chart */}
        <SectionTitle>Shortfall Trend</SectionTitle>
        <Card>
          <div className="flex items-end gap-[2px] h-32">
            {shortfallByDay.map((d, i) => {
              const val = Math.max(d.diff, 0)
              const h = maxShortfall > 0 ? (val / maxShortfall) * 100 : 0
              return (
                <div key={d.date} className="flex-1 flex flex-col items-center justify-end group relative" title={`${formatShortDate(d.date)}: ${formatR(val)}`}>
                  <div
                    className={`w-full rounded-t ${val > 2 ? 'bg-red-500/70' : 'bg-green-500/30'}`}
                    style={{ height: `${Math.max(h, 2)}%`, minHeight: val > 0 ? '2px' : '1px' }}
                  />
                </div>
              )
            })}
          </div>
          <div className="flex justify-between text-[10px] text-gray-500 mt-1">
            <span>{formatShortDate(dates[0] || '')}</span>
            <span>{formatShortDate(dates[dates.length - 1] || '')}</span>
          </div>
        </Card>

        {/* SECTION 1 — Daily Shortfall Summary */}
        <SectionTitle>Daily Shortfall Summary</SectionTitle>
        <Card className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr>
                <Th>Date</Th>
                <Th className="text-right">Projected</Th>
                <Th className="text-right">Consumed</Th>
                <Th className="text-right">Surplus</Th>
                <Th className="text-right">Shortfall</Th>
                <Th className="text-center">Status</Th>
              </tr>
            </thead>
            <tbody>
              {shortfallByDay.map(d => (
                <tr key={d.date} className={d.status === 'short' ? 'bg-red-950/20' : ''}>
                  <Td>{formatShortDate(d.date)} {getDayName(d.date)}</Td>
                  <Td className="text-right">{formatR(d.projected)}</Td>
                  <Td className="text-right">{formatR(d.consumed)}</Td>
                  <Td className="text-right">{formatR(d.surplus)}</Td>
                  <Td className={`text-right font-medium ${d.status === 'short' ? 'text-red-400' : 'text-green-400'}`}>{formatR(Math.max(d.diff, 0))}</Td>
                  <Td className="text-center">
                    {d.status === 'short'
                      ? <Badge label="SHORT" color="red" />
                      : <Badge label="OK" color="green" />}
                  </Td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="border-t-2 border-gray-700 font-semibold">
                <Td>Total ({dates.length} days)</Td>
                <Td className="text-right">{formatR(shortfallByDay.reduce((s, d) => s + d.projected, 0))}</Td>
                <Td className="text-right">{formatR(shortfallByDay.reduce((s, d) => s + d.consumed, 0))}</Td>
                <Td className="text-right">{formatR(shortfallByDay.reduce((s, d) => s + d.surplus, 0))}</Td>
                <Td className={`text-right font-bold ${totalShortfall > 0 ? 'text-red-400' : 'text-green-400'}`}>{formatR(totalShortfall)}</Td>
                <Td />
              </tr>
            </tfoot>
          </table>
        </Card>

        {/* SECTION 2 — Shortfall by Category */}
        {catRanking.length > 0 && (<>
          <SectionTitle>Shortfall by Category</SectionTitle>
          <Card className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr>
                  <Th>Category</Th>
                  <Th className="text-right">Days Short</Th>
                  <Th className="text-right">Total Short</Th>
                  <Th className="text-right">Avg / Day</Th>
                  <Th className="text-right">Worst Day</Th>
                  <Th />
                </tr>
              </thead>
              <tbody>
                {catRanking.map(c => {
                  const isOpen = expandedCat === c.name
                  return (
                    <React.Fragment key={c.name}>
                      <tr className="cursor-pointer hover:bg-gray-800/30" onClick={() => setExpandedCat(isOpen ? null : c.name)}>
                        <Td>
                          <span className="flex items-center gap-1 capitalize">
                            {isOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                            {c.label}
                          </span>
                        </Td>
                        <Td className="text-right">{c.daysShort}</Td>
                        <Td className="text-right text-red-400 font-medium">{formatR(c.totalShort)}</Td>
                        <Td className="text-right">{formatR(c.avgShort)}</Td>
                        <Td className="text-right">{formatR(c.worst.diff)} <span className="text-gray-500 text-xs">({formatShortDate(c.worst.date)})</span></Td>
                        <Td><HorizontalBar value={c.totalShort} max={catRanking[0]?.totalShort || 1} color="bg-red-500" /></Td>
                      </tr>
                      {/* SECTION 5 — Category Drill-Down */}
                      {isOpen && (
                        <tr>
                          <td colSpan={6} className="p-0">
                            <div className="bg-gray-800/30 px-4 py-3 border-y border-gray-700">
                              <table className="w-full text-xs">
                                <thead>
                                  <tr className="text-gray-500">
                                    <th className="text-left py-1 px-2">Date</th>
                                    <th className="text-right py-1 px-2">Projected</th>
                                    <th className="text-right py-1 px-2">Consumed</th>
                                    <th className="text-right py-1 px-2">Surplus</th>
                                    <th className="text-right py-1 px-2">Shortfall</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {c.days.sort((a, b) => a.date.localeCompare(b.date)).map(d => (
                                    <tr key={d.date} className="border-b border-gray-800/30">
                                      <td className="py-1 px-2">{formatShortDate(d.date)} {getDayName(d.date)}</td>
                                      <td className="py-1 px-2 text-right">{formatR(d.projected)}</td>
                                      <td className="py-1 px-2 text-right">{formatR(d.consumed)}</td>
                                      <td className="py-1 px-2 text-right">{formatR(d.surplus)}</td>
                                      <td className="py-1 px-2 text-right text-red-400 font-medium">{formatR(d.diff)}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  )
                })}
              </tbody>
            </table>
          </Card>
        </>)}

        {/* SECTION 4 — Shortfall by Day of Week */}
        <SectionTitle>Shortfall by Day of Week</SectionTitle>
        <Card>
          <div className="grid grid-cols-7 gap-2">
            {DOW.map(dow => {
              const d = dowShortfall[dow] || { total: 0, count: 0, shortCount: 0 }
              const avg = d.shortCount > 0 ? d.total / d.shortCount : 0
              return (
                <div key={dow} className="text-center">
                  <p className="text-xs text-gray-400 mb-1">{dow}</p>
                  <p className={`text-sm font-bold ${avg > 0 ? 'text-red-400' : 'text-green-400'}`}>{formatR(avg)}</p>
                  <p className="text-[10px] text-gray-500">{d.shortCount}/{d.count} days</p>
                </div>
              )
            })}
          </div>
        </Card>

        {/* ───────────────────────────────────────── */}
        {/* Discount Losses — separate from stock shortfall. Lost   */}
        {/* revenue from items sold below their standard price.    */}
        {/* Hidden entirely when no discounts in the period.       */}
        {/* ───────────────────────────────────────── */}
        {(totalDiscountLoss > 0 || totalFreeLoss > 0) && (
          <>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <KpiCard
                label="Discount Losses"
                value={formatR(totalDiscountLoss)}
                sub={`${discountDays.length} day${discountDays.length !== 1 ? 's' : ''} affected`}
                icon={AlertTriangle}
                color={totalDiscountLoss > 0 ? 'text-red-400' : 'text-green-400'}
              />
              <KpiCard
                label="Avg / Discount Day"
                value={formatR(avgDailyDiscount)}
                sub="on days with discounts"
                icon={Target}
              />
              <KpiCard
                label="Worst Day"
                value={worstDiscountDay ? formatR(worstDiscountDay.totalDiscount) : '—'}
                sub={worstDiscountDay ? formatShortDate(worstDiscountDay.date) : ''}
                icon={Calendar}
                color={worstDiscountDay ? 'text-red-400' : 'text-gray-400'}
              />
              <KpiCard
                label="Most Discounted"
                value={mostDiscountedProduct ? mostDiscountedProduct.productName : '—'}
                sub={mostDiscountedProduct ? `${formatR(mostDiscountedProduct.totalDiscount)} lost` : ''}
                icon={ShoppingBag}
                color={mostDiscountedProduct ? 'text-amber-400' : 'text-gray-400'}
              />
            </div>

            {totalFreeLoss > 0 && (
              <div className="bg-amber-950/30 border border-amber-700/40 rounded px-4 py-2 text-xs text-amber-200">
                <strong className="text-amber-100">{formatR(totalFreeLoss)}</strong> in items given away free during this period (tracked separately from discounts).
              </div>
            )}

            {discountProductRanking.length > 0 && (
              <Card>
                <div className="mb-3">
                  <h3 className="text-sm font-semibold text-white">Top Discounted Products</h3>
                  <p className="text-xs text-gray-500">Items most frequently sold below standard price — review for staff training opportunities</p>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead className="text-gray-400 border-b border-gray-800">
                      <tr>
                        <th className="text-left py-2 px-2">Product</th>
                        <th className="text-right py-2 px-2">Days</th>
                        <th className="text-right py-2 px-2">Total Qty</th>
                        <th className="text-right py-2 px-2">Total Lost</th>
                      </tr>
                    </thead>
                    <tbody>
                      {discountProductRanking.slice(0, 10).map(p => (
                        <tr key={p.productCode} className="border-b border-gray-800/60">
                          <td className="py-1.5 px-2 text-gray-200">
                            {p.productName}
                            <span className="text-gray-600 ml-1.5 font-mono text-[10px]">{p.productCode}</span>
                          </td>
                          <td className="py-1.5 px-2 text-right text-gray-300">{p.days}</td>
                          <td className="py-1.5 px-2 text-right text-gray-300">{p.qty}</td>
                          <td className="py-1.5 px-2 text-right text-red-400 font-medium">{formatR(p.totalDiscount)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </Card>
            )}
          </>
        )}
      </div>
    )
  }

  // ─── RENDER ───

  return (
    <div className="min-h-screen bg-gray-950 pb-20">
      {/* Header */}
      <div className="px-4 pt-6 pb-4 max-w-7xl mx-auto">
        <h1 className="text-white text-xl font-bold mb-1">Business Analytics</h1>
        <p className="text-gray-400 text-sm">Comprehensive insights for <span className="text-orange-400 capitalize">{dept}</span></p>
      </div>

      {/* Date Range Picker */}
      <div className="px-4 max-w-7xl mx-auto mb-4">
        <Card className="flex flex-wrap items-center gap-3 p-3">
          <span className="text-gray-400 text-xs">From</span>
          <DateInput value={startDate} onChange={setStartDate} />
          <span className="text-gray-400 text-xs">To</span>
          <DateInput value={endDate} onChange={setEndDate} />
          <span className="text-gray-500 text-xs ml-auto">{data.dates.length} days</span>
        </Card>
      </div>

      {/* Tab Buttons */}
      <div className="px-4 max-w-7xl mx-auto mb-6">
        <div className="flex gap-1 overflow-x-auto pb-1 scrollbar-none">
          {TABS.map(t => {
            const Icon = t.icon
            return (
              <button
                key={t.id}
                onClick={() => setTab(t.id)}
                className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium whitespace-nowrap transition-colors ${
                  tab === t.id
                    ? 'bg-orange-500/20 text-orange-400 border border-orange-500/30'
                    : 'text-gray-400 hover:text-white hover:bg-gray-800 border border-transparent'
                }`}
              >
                <Icon size={14} />
                {t.label}
              </button>
            )
          })}
        </div>
      </div>

      {/* Tab Content */}
      <div className="px-4 max-w-7xl mx-auto">
        {tab === 'revenue' && <RevenueTab />}
        {tab === 'products' && <ProductsTab />}
        {tab === 'shortfall' && <ShortfallTab />}
        {tab === 'waste' && <WasteTab />}
        {tab === 'financial' && <FinancialTab />}
        {tab === 'ops' && <OpsTab />}
        {tab === 'pap' && <PapAnalyticsSection department={dept} dateFrom={startDate} dateTo={endDate} />}
      </div>
    </div>
  )
}
